import { Injectable } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { PVT_CONSTANTS } from '../common/constants';
import { MESSAGES_CONSTANTS as MC } from '../common/messages-constants';
import { NUMBERS_CONSTANTS } from '../common/numbers-constants';
import { LabTestRepeat, LabTestWithSampleDetails } from '../models/labTestRepeat';

export interface GenerateReportPopupData {
  groupedData: LabTestWithSampleDetails[];
  projectID: number;
}

export interface GenerateReportSelectionPayload {
  finalArray: LabTestRepeat[];
  pdfSelected: boolean;
  excelSelected: boolean;
  projectID: number;
  preview:boolean;
}

export interface GenerateReportSelectionResult {
  payload?: GenerateReportSelectionPayload;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GenerateReportPopupService {
  private selectionByLabTestId = new Map<number, SelectionModel<LabTestRepeat>>();

  ResetSelectionState(): void {
    this.selectionByLabTestId.clear();
  }

  CreateDataSource(groupedData: LabTestWithSampleDetails[]): MatTableDataSource<LabTestWithSampleDetails, MatPaginator> {
    return new MatTableDataSource(groupedData);
  }

  AttachPaginator<T>(dataSource: MatTableDataSource<T, MatPaginator>, paginator: MatPaginator): void {
    dataSource.paginator = paginator;
  }

  Drop<T>(dataSource: MatTableDataSource<T, MatPaginator>, paginator: MatPaginator, event: CdkDragDrop<LabTestRepeat[]>): void {
    const pageIndex = paginator.pageIndex;
    const pageSize = paginator.pageSize;
    const previousIndex = pageIndex * pageSize + event.previousIndex;
    const currentIndex = pageIndex * pageSize + event.currentIndex;
    moveItemInArray(dataSource.data, previousIndex, currentIndex);
    dataSource.data = [...dataSource.data];
  }

  GetSelection(labTestId: number): SelectionModel<LabTestRepeat> {
    let selection = this.selectionByLabTestId.get(labTestId);
    if (!selection) {
      selection = new SelectionModel<LabTestRepeat>(true, []);
      this.selectionByLabTestId.set(labTestId, selection);
    }
    return selection;
  }

  IsAllSelected(labTest: LabTestWithSampleDetails): boolean {
    const selection = this.GetSelection(labTest.labTestId);
    const numSelected = selection.selected.length;
    const numRows = labTest.labTestRepeat.length;
    return numRows > NUMBERS_CONSTANTS.zero && numSelected === numRows;
  }

  MasterToggle(labTest: LabTestWithSampleDetails): void {
    const selection = this.GetSelection(labTest.labTestId);
    if (this.IsAllSelected(labTest)) {
      selection.clear();
      return;
    }

    labTest.labTestRepeat.forEach((row) => selection.select(row));
  }

  ToggleRow(labTestId: number, row: LabTestRepeat): void {
    this.GetSelection(labTestId).toggle(row);
  }

  BuildSelectionResult(
    groupedData: LabTestWithSampleDetails[],
    pdfSelected: boolean,
    excelSelected: boolean,
    projectID: number
  ): GenerateReportSelectionResult {
    const selectedAll: LabTestRepeat[] = [];

    for (const labTest of groupedData) {
      selectedAll.push(...this.GetSelection(labTest.labTestId).selected);
    }

    if (selectedAll.length === NUMBERS_CONSTANTS.zero) {
      return { errorMessage: MC.selectTheExperiment };
    }

    const finalArray = selectedAll.filter(
      (selection) => selection.statusName.toLowerCase() === PVT_CONSTANTS.Accepted.toLowerCase()
    );

    if (finalArray.length === NUMBERS_CONSTANTS.zero) {
      return { errorMessage: MC.selectTheExperiment };
    }

    if (!pdfSelected && !excelSelected) {
      return { errorMessage: MC.selectThePdfOrExcelTypeForReport };
    }

    return {
      payload: {
        finalArray,
        pdfSelected,
        excelSelected,
        projectID,
        preview: false
      },
    };
  }
}
